import { PageNavigation } from "../../../components/layout/PageNavigation";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, ui } from "../../../components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { cn } from "../../../lib/utils";
import sessionDB from "../../../utils/sessionDB";
import { getAuthenticatedEmployeeId } from "../../../lib/employee";
import { conveyanceClaimService } from "../../../services/conveyanceClaimService";
import { isYearlyFrequency } from "../constants/conveyanceConstants";
import {
  CLAIMS_LIST_PATH,
  CLAIM_CATEGORY,
  CLAIM_STATUS,
  MAX_TOTAL_AMOUNT,
  VEHICLE_TYPE,
  VEHICLE_TYPE_LABEL_KEY,
  abbreviationFor,
  defaultCurrency,
  formatAmount,
  isSubmitterWritable,
} from "../constants/claimConstants";
import {
  attachmentsFromStored,
  duplicateWindowError,
  encodeAttachments,
  firstOfMonth,
  maintenanceCapFor,
  maintenanceWindowKey,
  toIsoDate,
} from "../lib/claimRules";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import {
  ClaimBlockedScreen,
  ClaimLoadingScreen,
  useConveyancePolicy,
} from "../components/PolicyGate";
import { Toast } from "../components/Toast";
import {
  CurrencyAmountField,
  Field,
  ReadOnlyValue,
  borderFor,
  selectBaseClass,
} from "../components/formControls";
import { ProofAttachments } from "../components/ProofAttachments";
import { ClaimApprovalHistory } from "../components/ClaimApprovalHistory";

/**
 * Screen 5 — Claim Submission Form: Vehicle Maintenance (ESS).
 *
 * Available whenever the policy has maintenance_supported = true, INDEPENDENT
 * of the Claim Type toggle — a tenant on Fixed monthly reaches this screen just
 * as a tenant on Rate per km does.
 *
 * Vehicle Type is honour-system: there is no vehicle master and no ownership
 * check anywhere, so the employee self-declares. What the declaration does
 * decide is the cap, because bike and car carry separate, independently
 * configured maxima — switching the dropdown re-validates the amount against
 * the other cap.
 *
 * One claim per vehicle type per frequency window. The window key written to
 * maintenance_frequency_window is the calendar month or the Indian financial
 * year, per the policy's configured frequency; the two vehicle types are
 * tracked as independent windows.
 */
export function VehicleMaintenanceClaimPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { claimId } = useParams();
  const isEdit = Boolean(claimId);

  const {
    loading: policyLoading,
    error: policyError,
    policy,
    currencies,
    isConfigured,
    reload: reloadPolicy,
  } = useConveyancePolicy();

  const empId = useMemo(() => {
    const fromSession = Number(sessionDB.getEmpId());
    if (Number.isFinite(fromSession) && fromSession > 0) return fromSession;
    return getAuthenticatedEmployeeId();
  }, []);

  const [claim, setClaim] = useState(null);
  const [loadingClaim, setLoadingClaim] = useState(isEdit);
  const [loadError, setLoadError] = useState("");

  const [vehicleType, setVehicleType] = useState("");
  const [amount, setAmount] = useState("");
  const [proofs, setProofs] = useState([]);
  const [errors, setErrors] = useState({});
  const [metricId, setMetricId] = useState(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [toast, setToast] = useState(null);

  /** Date is the creation date; an existing claim keeps the one it was raised on. */
  const claimDate = useMemo(
    () => (claim?.claimDate ? String(claim.claimDate).slice(0, 10) : toIsoDate(new Date())),
    [claim]
  );

  const windowKey = useMemo(
    () => maintenanceWindowKey(claimDate, policy?.maintenanceFrequency),
    [claimDate, policy]
  );

  const cap = useMemo(() => maintenanceCapFor(policy, vehicleType), [policy, vehicleType]);

  useEffect(() => {
    if (!isEdit) return;
    setLoadingClaim(true);
    conveyanceClaimService
      .getClaimById(claimId)
      .then((loaded) => {
        setClaim(loaded);
        setVehicleType(loaded?.vehicleType || "");
        setAmount(
          loaded?.totalAmount === null || loaded?.totalAmount === undefined
            ? ""
            : String(loaded.totalAmount)
        );
        // The receipts are stored on the claim itself, so reopening the claim
        // puts them back in the proofs list — downloadable, and replaceable.
        setProofs(attachmentsFromStored(loaded?.attachments));
      })
      .catch((error) => {
        console.error("Failed to load claim:", error);
        setLoadError(error.message || t("conveyanceClaim.errorLoadClaim"));
      })
      .finally(() => setLoadingClaim(false));
  }, [claimId, isEdit, t]);

  // Starts on the default currency; an existing claim keeps the metric it
  // was saved with.
  useEffect(() => {
    if (metricId != null) return;
    if (claim?.metricId != null) setMetricId(claim.metricId);
    else if (currencies.length) setMetricId(defaultCurrency(currencies)?.metricId ?? null);
  }, [claim, currencies, metricId]);

  const status = claim?.claimStatus ?? CLAIM_STATUS.DRAFT;
  const readOnly = isEdit && !isSubmitterWritable(status);
  const numericAmount = Number(amount);
  const abbreviation = abbreviationFor(currencies, metricId);
  // Once a month or once a year — the policy decides which window the backend
  // enforces, so it also decides how a rejection is worded.
  const yearlyWindow = isYearlyFrequency(policy?.maintenanceFrequency);

  /** Proof is always mandatory here — no conditional exception. */
  const validate = () => {
    const next = {};
    if (!vehicleType) next.vehicleType = t("conveyanceClaim.errorVehicleTypeRequired");
    if (!(numericAmount > 0)) next.amount = t("conveyanceClaim.errorAmountGreaterThanZero");
    else if (cap !== null && numericAmount > cap) {
      next.amount = t("conveyanceClaim.errorAboveCap", {
        cap: formatAmount(cap, abbreviation),
        vehicle: t(VEHICLE_TYPE_LABEL_KEY[vehicleType]),
      });
    } else if (numericAmount > MAX_TOTAL_AMOUNT) {
      next.amount = t("conveyanceClaim.errorTotalOverCap", { max: MAX_TOTAL_AMOUNT });
    }
    if (proofs.length === 0) next.proofs = t("conveyanceClaim.errorProofRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const persist = async (claimStatus) => {
    if (claimStatus !== CLAIM_STATUS.DRAFT && !validate()) return;

    setSaving(true);
    setSaveError("");
    const payload = {
      category: CLAIM_CATEGORY.MAINTENANCE,
      claimDate,
      claimMonth: toIsoDate(firstOfMonth(claimDate)),
      totalAmount: Number.isFinite(numericAmount) ? numericAmount : 0,
      claimStatus,
      policyId: policy?.policyId ?? null,
      metricId: metricId ?? null,
      vehicleType: vehicleType || null,
      maintenanceFrequencyWindow: windowKey || null,
      empId,
      // The receipts are saved with the claim as its complete set — there is
      // no proof endpoint — so a receipt the user removed is simply left out.
      attachments: await encodeAttachments(proofs),
      ...(isEdit ? { modifiedBy: empId } : { createdBy: empId }),
    };

    const request = isEdit
      ? conveyanceClaimService.updateClaim(claimId, payload)
      : conveyanceClaimService.createClaim(payload);

    request
      .then((saved) => {
        setClaim(saved);
        setToast({
          tone: "success",
          message:
            claimStatus === CLAIM_STATUS.DRAFT
              ? t("conveyanceClaim.draftSaved")
              : t("conveyanceClaim.claimSubmitted"),
        });
        // Draft and Submit both end on the claims list, once the toast has
        // been seen.
        setTimeout(() => navigate(CLAIMS_LIST_PATH), 900);
      })
      .catch((error) => {
        console.error("Failed to save claim:", error);
        const duplicate = duplicateWindowError(error, {
          yearly: yearlyWindow,
          vehicle: vehicleType ? t(VEHICLE_TYPE_LABEL_KEY[vehicleType]) : "",
        });
        setSaveError(
          duplicate
            ? t(duplicate.messageKey, duplicate.values)
            : error.message || t("conveyanceClaim.errorSaveClaim")
        );
      })
      .finally(() => setSaving(false));
  };

  /* ------------------------------------------------------------- gating */

  if (policyLoading || loadingClaim) return <ClaimLoadingScreen />;

  if (policyError) {
    return (
      <ClaimBlockedScreen
        title={t("conveyanceClaim.policyUnavailableTitle")}
        message={policyError}
        onRetry={reloadPolicy}
      />
    );
  }

  if (!isConfigured) {
    return (
      <ClaimBlockedScreen
        title={t("conveyanceClaim.noPolicyTitle")}
        message={t("conveyanceClaim.noPolicyMessage")}
        onRetry={reloadPolicy}
      />
    );
  }

  // Availability depends only on the maintenance flag, never on the Claim Type.
  if (!policy.maintenanceSupported) {
    return (
      <ClaimBlockedScreen
        title={t("conveyanceClaim.categoryUnavailableTitle")}
        message={t("conveyanceClaim.maintenanceUnavailableMessage")}
      />
    );
  }

  if (loadError) {
    return <ClaimBlockedScreen title={t("conveyanceClaim.errorLoadClaim")} message={loadError} />;
  }

  return (
    <AppLayout className="space-y-5">
      {toast ? (
        <Toast tone={toast.tone} onDone={() => setToast(null)}>
          {toast.message}
        </Toast>
      ) : null}


      <PageNavigation>
        <div className="page-header w-full">
          <PageTitleRow back={<PageBackLink to={CLAIMS_LIST_PATH} />}>
            <h1 className={ui.pageTitle}>{t("conveyanceClaim.maintenanceFormTitle")}</h1>
          </PageTitleRow>
        </div>
      </PageNavigation>

      {status === CLAIM_STATUS.SENT_BACK ? (
        <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-bold text-amber-900">{t("conveyanceClaim.sentBackTitle")}</p>
            <p className="mt-0.5 text-[12px] text-amber-800">{t("conveyanceClaim.sentBackMessage")}</p>
          </div>
        </div>
      ) : null}

      {claim ? <ClaimApprovalHistory claim={claim} /> : null}

      {saveError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <p className="text-sm font-semibold text-rose-700">{saveError}</p>
        </div>
      ) : null}

      <ContentCard
        title={t("conveyanceClaim.claimDetail")}
        action={<ClaimStatusBadge status={status} />}
        bodyClassName="p-4 sm:p-6 space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <Field label={t("conveyanceClaim.date")} hint={t("conveyanceClaim.dateAutoHint")}>
            <ReadOnlyValue>{claimDate}</ReadOnlyValue>
          </Field>

          <Field
            label={t("conveyanceClaim.vehicleType")}
            required
            error={errors.vehicleType}
            hint={t("conveyanceClaim.vehicleTypeHonourHint")}
          >
            <Select
              value={vehicleType || undefined}
              disabled={readOnly}
              onValueChange={(value) => {
                setVehicleType(value);
                setErrors((current) => ({ ...current, vehicleType: undefined, amount: undefined }));
              }}
            >
              <SelectTrigger className={cn(selectBaseClass, borderFor(errors.vehicleType))}>
                <SelectValue placeholder={t("conveyanceClaim.selectVehicleType")} />
              </SelectTrigger>
              <SelectContent>
                {Object.values(VEHICLE_TYPE).map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(VEHICLE_TYPE_LABEL_KEY[type])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field
            label={t("conveyanceClaim.amount")}
            required
            error={errors.amount}
            hint={
              cap !== null
                ? t("conveyanceClaim.capHint", { cap: formatAmount(cap, abbreviation) })
                : t("conveyanceClaim.capUnknownHint")
            }
          >
            <CurrencyAmountField
              value={amount}
              onValueChange={setAmount}
              currencies={currencies}
              metricId={metricId}
              onMetricChange={setMetricId}
              disabled={readOnly}
              error={errors.amount}
            />
          </Field>
        </div>

        <Field
          label={t("conveyanceClaim.proofs")}
          required
          error={errors.proofs}
          hint={t("conveyanceClaim.maintenanceProofHint")}
        >
          <ProofAttachments
            proofs={proofs}
            onChange={(next) => {
              setProofs(next);
              setErrors((current) => ({ ...current, proofs: undefined }));
            }}
            disabled={readOnly || saving}
            readOnly={readOnly}
            error={errors.proofs}
          />
        </Field>
      </ContentCard>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-(--color-text-border)/70 bg-white p-4 sm:p-6">
        <div>
          <p className={ui.sectionLabel}>{t("conveyanceClaim.claimTotal")}</p>
          <p className="text-xl font-extrabold text-foreground">
            {formatAmount(Number.isFinite(numericAmount) ? numericAmount : 0, abbreviation)}
          </p>
        </div>

        {readOnly ? (
          <p className="text-[11px] per-file-hint max-w-[360px]">
            {t("conveyanceClaim.readOnlyNotice")}
          </p>
        ) : (
          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={() => persist(CLAIM_STATUS.DRAFT)}
              disabled={saving}
              className="h-11 px-5 rounded-xl border border-(--color-text-border) text-sm font-semibold text-foreground hover:bg-gray-50 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("conveyanceClaim.saveDraft")}
            </button>
            <button
              type="button"
              onClick={() => persist(CLAIM_STATUS.SUBMITTED)}
              disabled={saving}
              className={ui.primaryBtn}
            >
              {saving ? t("localConveyance.saving") : t("conveyanceClaim.submitClaim")}
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export default VehicleMaintenanceClaimPage;
